import { JitsiMeeting } from '@jitsi/react-sdk';
import { IJitsiMeetExternalApi } from '@jitsi/react-sdk/lib/types';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { CCard, CContainedButton, CLottie } from 'components/atoms';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePetOwnerDetailQuery } from 'services/pet-owner';
import { useCheckSocketMutation, useLoginVideoCallMutation } from 'services/video-call';
import { resetVideoCall, setVideoCall } from 'store/app/app-slice';
import constants from 'utils/constants';

type Props = {
  videoCallGroupId: string;
  protocolNo: number;
  appointmentDate: string;
  doctorCode: string;
  patientName: string;
};

const Jitsi = forwardRef(
  ({ videoCallGroupId, protocolNo, appointmentDate, doctorCode, patientName }: Props, ref) => {
    const { t } = useTranslation(['translation', 'common']);
    const dispatch = useAppDispatch();
    const { data, isLoading, isSuccess } = usePetOwnerDetailQuery();
    const [connection, setConnection] = useState<HubConnection>();
    const [jitsiMeetApi, setJitsiMeetApi] = useState<IJitsiMeetExternalApi>();
    const videoCall = useAppSelector((state) => state.app.videoCall);

    const initialState = {
      isDoctorAttended: false,
      message: t('video-call.lobby'),
    };
    const [state, setState] = useState(initialState);

    const [loginVideoCall] = useLoginVideoCallMutation();
    const [checkSocket] = useCheckSocketMutation();

    useImperativeHandle(
      ref,
      () => {
        return {
          close() {
            handleEndCall();
          },
          minimize() {
            handleMinimize();
          },
          fullScreen() {
            handleFullScreen();
          },
        };
      },
      [jitsiMeetApi, videoCall]
    );

    function handleMinimize() {
      videoCall.screen.isDraggable
        ? dispatch(
            setVideoCall({
              ...videoCall,
              screen: {
                ...videoCall.screen,
                isFullScreen: false,
                isDraggable: false,
                isCentred: true,
              },
            })
          )
        : dispatch(
            setVideoCall({
              ...videoCall,
              screen: {
                ...videoCall.screen,
                isFullScreen: false,
                isDraggable: true,
                isCentred: false,
              },
            })
          );
    }

    function handleFullScreen() {
      videoCall.screen.isFullScreen
        ? dispatch(
            setVideoCall({
              ...videoCall,
              screen: {
                ...videoCall.screen,
                isFullScreen: false,
                isDraggable: false,
                isCentred: true,
              },
            })
          )
        : dispatch(
            setVideoCall({
              ...videoCall,
              screen: {
                ...videoCall.screen,
                isFullScreen: true,
                isDraggable: false,
                isCentred: false,
              },
            })
          );
    }

    function handleEndCall() {
      jitsiMeetApi?.dispose();
      setState(initialState);
      dispatch(resetVideoCall());
    }

    function createHubConnection() {
      const connection = new HubConnectionBuilder()
        .withUrl(`${constants.baseUrl}/vitalHub`)
        .withAutomaticReconnect()
        // .configureLogging(LogLevel.Information)
        .build();

      setConnection(connection);
    }

    useEffect(() => {
      if (connection) {
        (async () => {
          try {
            await connection.start();

            await loginVideoCall({
              connectionId: connection.connectionId!,
              currentRoom: videoCallGroupId,
              username: protocolNo.toString(),
              isActiveRoom: false,
            }).unwrap();

            const { isSuccess } = await checkSocket({
              roomName: videoCallGroupId,
              appointmentDate,
              doctorCode,
              patientName,
            }).unwrap();

            setState((prev) => ({
              ...prev,
              message: isSuccess ? t('video-call.joining') : t('video-call.lobby'),
              isDoctorAttended: isSuccess,
            }));

            dispatch(
              setVideoCall({
                ...videoCall,
                isDoctorAttended: isSuccess,
                screen: {
                  ...videoCall.screen,
                  isCentred: isSuccess,
                },
              })
            );

            connection.on('GetConnectionId', (data) => {
              let isConnectionIdMatch = false;
              const clientConnectionId = connection.connectionId;
              const serverConnectionId = data;

              if (data === connection.connectionId) {
                isConnectionIdMatch = true;
                console.log({ isConnectionIdMatch }, { connectionId: connection.connectionId });
              } else {
                console.warn({ isConnectionIdMatch }, { clientConnectionId }, { serverConnectionId });
              }
            });

            connection.on(
              'StartPatientAppointmentCall',
              (data: {
                doctorCode: string;
                groupId: string;
                protocolNo: string;
                requesterId: number;
                requesterName: string;
              }) => {
                console.log('StartPatientAppointmentCall', data);

                if (data) {
                  setState((prev) => ({
                    ...prev,
                    message: t('video-call.joining'),
                  }));

                  setTimeout(() => {
                    // to prevent the patient from becoming a moderator if he joins before the doctor
                    setState((prev) => ({
                      ...prev,
                      isDoctorAttended: true,
                    }));

                    dispatch(
                      setVideoCall({
                        ...videoCall,
                        isDoctorAttended: true,
                        screen: {
                          ...videoCall.screen,
                          isCentred: true,
                        },
                      })
                    );
                  }, 5000);
                } else {
                  setState(initialState);
                }
              }
            );

            connection.on('endCall', (data) => {
              console.log('endCall', data);
              handleEndCall();
            });
          } catch (error) {
            console.error(error);
          }
        })();
      } else {
        createHubConnection();
      }

      return () => {
        connection?.stop();
      };
    }, [connection]);

    if (isLoading) return <CLottie isFullPage />;

    if (isSuccess) {
      const user = data.data;

      return (
        <div
          className={`flex flex-col items-center justify-center gap-4 ${
            state.isDoctorAttended ? 'h-full' : 'w-[90vw] max-w-sm'
          }`}
        >
          {state.isDoctorAttended ? (
            <JitsiMeeting
              domain={constants.meetUrl}
              roomName={videoCallGroupId}
              configOverwrite={{
                startWithAudioMuted: true,
                disableModeratorIndicator: true,
                startScreenSharing: true,
                enableEmailInStats: false,
                disableProfile: true,
                prejoinConfig: {
                  enabled: true,
                  hideDisplayName: true,
                },
              }}
              interfaceConfigOverwrite={{
                DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,
              }}
              userInfo={{
                displayName: user.fullName,
                email: user.email,
              }}
              onApiReady={(externalApi) => {
                setJitsiMeetApi(externalApi);

                externalApi.on('videoConferenceLeft', (data) => {
                  console.log(data);
                  handleEndCall();
                });
              }}
              getIFrameRef={(iframeRef) => {
                iframeRef.style.height = '100%';
                iframeRef.style.width = '100%';
              }}
            />
          ) : (
            <CCard className="flex min-h-[300px] flex-col items-center justify-center gap-4 p-4 text-center text-label20 text-neutral-400 shadow-none md:gap-10 md:p-10">
              <CLottie isFullPage animation="hourglass" />

              <div>
                <p>{state.message}</p>

                <p className="mt-3">{t('common:please-wait')}</p>
              </div>

              <CContainedButton
                className="max-w-xs !bg-danger hover:!bg-danger/90"
                fullWidth
                onClick={handleEndCall}
              >
                {t('video-call.end')}
              </CContainedButton>
            </CCard>
          )}
        </div>
      );
    }

    return null;
  }
);

export default Jitsi;

